'use client';
import React from 'react';
import { A11y, FreeMode, Navigation, Thumbs, Virtual } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/virtual';
import './swiper.css';
import { ImageInfo } from '@/components/data/dataType';
import { cn } from '@/lib/utils';
import { sortAscBy } from '@/app/[locale]/admin/lib/util';
import NextImage from '../Image/NextImage';

type ConditionalWrapperProps = {
    children: React.ReactElement;
    condition: boolean;
    wrapper: (children: React.ReactElement) => JSX.Element;
};

const ConditionalWrapper: React.FC<ConditionalWrapperProps> = ({
    condition,
    wrapper,
    children,
}) => (condition ? wrapper(children) : children);

type ImageProps = {
    id: string | number;
    image: ImageInfo;
    title?: string;
    subtitle?: string;
    buttonText?: string;
    link?: string;
};

const SwiperThumbs: React.FC<{
    buttonColor?: string;
    imageClass?: string;
    data: ImageProps[];
    imageSource?: string;
    locale: string;
    onSlideChanged?: (swiper: number) => void;
    sizes?: string;
}> = ({
    buttonColor = '#fff',
    imageClass = 'mx-auto block max-h-[60vh] object-cover',
    data,
    imageSource = 'supabase',
    locale,
    onSlideChanged,
    sizes = '(max-width: 1024px) 100vw, (max-width: 1536px) 71vw, 66vw',
}) => {
    const [loading, setLoading] = React.useState(true);
    const [isClient, setIsClient] = React.useState(false);
    const [thumbsSwiper, setThumbsSwiper] = React.useState(null);
    const [activeIndex, setActiveIndex] = React.useState<number>(0);
    const swiperRef = React.useRef<SwiperRef>(null);

    React.useEffect(() => {
        setIsClient(true);
    }, []);

    if (!isClient) {
        return null;
    }

    let sortedImages: ImageProps[] = [];
    if (imageSource === 'supabase') {
        sortedImages = sortAscBy(data, 'position').map(
            (imageInfo: ImageInfo) => ({
                ...imageInfo,
                src: `${imageInfo.bucket}/${imageInfo.folder}/${imageInfo.itemId}/images/${imageInfo.name}`,
            }),
        );
    } else {
        sortedImages = sortAscBy(data, 'position');
    }

    const navStyle = {
        '--swiper-navigation-color': buttonColor,
        '--swiper-pagination-color': buttonColor,
    };

    return (
        <div className="h-full relative my-auto">
            {loading && (
                <div className="relative h-64 inset-0 flex items-center justify-center bg-transparent">
                    <div className="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-gray-900"></div>
                </div>
            )}
            <Swiper
                autoHeight={true}
                modules={[FreeMode, Navigation, A11y, Thumbs, Virtual]}
                navigation
                onAfterInit={() => setLoading(false)}
                onSlideChange={swiper => {
                    setActiveIndex(swiper.activeIndex);
                    onSlideChanged(swiper.activeIndex);
                }}
                ref={swiperRef}
                slidesPerView={1}
                spaceBetween={0}
                style={navStyle as React.CSSProperties}
                thumbs={{ swiper: thumbsSwiper }}
                virtual
            >
                {sortedImages.map((item, index) => {
                    return (
                        item &&
                        item.image && (
                            <SwiperSlide key={item.id} virtualIndex={index}>
                                <ConditionalWrapper
                                    condition={
                                        !!(item?.link && item?.link[locale])
                                    }
                                    wrapper={children => <div>{children}</div>}
                                >
                                    <NextImage
                                        alt={
                                            item.image?.folder ||
                                            item.image?.name ||
                                            'Image'
                                        }
                                        className={`${imageClass} ${item.image.width > item.image.height ? 'w-full' : 'h-full'}`}
                                        image={item.image}
                                        lazy={index === 0 ? 'eager' : 'lazy'}
                                        priority={index === 0}
                                        sizes={sizes}
                                        style={{
                                            height: 'auto',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </ConditionalWrapper>
                            </SwiperSlide>
                        )
                    );
                })}
            </Swiper>
            <div className="mt-2 hidden sm:block">
                <Swiper
                    className="flex"
                    freeMode={true}
                    modules={[FreeMode, Navigation, Thumbs, Virtual]}
                    navigation
                    onClick={swiper => {
                        swiperRef?.current?.swiper.slideTo(swiper.clickedIndex);
                        swiper.slideTo(swiper.clickedIndex);
                        if (swiper.clickedIndex < activeIndex) {
                            swiper.slidePrev();
                        }
                        setActiveIndex(swiper.clickedIndex);
                    }}
                    onResize={swiper => {
                        setLoading(false);
                        swiper.update();
                    }}
                    onSwiper={setThumbsSwiper}
                    slidesPerView="auto"
                    style={navStyle as React.CSSProperties}
                    watchSlidesProgress
                >
                    {data.map((item, index) => {
                        return (
                            item &&
                            item.image && (
                                <SwiperSlide
                                    className="w-auto"
                                    key={index}
                                    virtualIndex={index}
                                >
                                    <div
                                        className={cn(
                                            'rounded flex flex-shrink-0 w-fit h-[144px] overflow-hidden shadow-md shadow-gray-200',
                                            activeIndex === index
                                                ? 'cursor-default border-2 border-gray-700'
                                                : 'cursor-pointer border border-gray-300',
                                        )}
                                        key={`thumbs-${item.image.id}`}
                                    >
                                        <NextImage
                                            alt={
                                                item.image?.folder ||
                                                item.image?.name ||
                                                'Image'
                                            }
                                            className="opacity-60"
                                            image={item.image}
                                            lazy={
                                                index === 0 ? 'eager' : 'lazy'
                                            }
                                            maxHeight={144}
                                            priority={index === 0}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                            }}
                                        />
                                    </div>
                                </SwiperSlide>
                            )
                        );
                    })}
                </Swiper>
            </div>
        </div>
    );
};

export default SwiperThumbs;
